import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Member button: a call-to-action button. Saved as a bare block placeholder with its settings as
// data-member-button-* attributes; Newsletters::RenderBlocks renders the real button (and
// Newsletters::MemberButtonHref re-checks the link) on the server. The editor chrome lives in
// dashboard/shared/tiptap/_member_button_editor.html.erb and its preview comes from the same
// server component. The block shows one view at a time (editor or preview, see block_views.js); a button
// with a label opens in its preview.

const PREVIEW_DELAY_MS = 300;
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS =/[\s\u0000-\u001f\u007f]/;

// Mirrors Newsletters::MemberButtonHref for custom links: https, or a path on the podcast's own host.
export function isSafeMemberButtonUrl(value) {
  const url = (value || '').trim();
  if (!url) return false;
  if (url.length > 2048 || UNSAFE_CHARACTERS.test(url)) return false;
  if (url.startsWith('/')) return !/^\/[/\\]/.test(url) && !url.includes('\\');
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname !== '' && !parsed.username && !parsed.password;
  } catch (_) {
    return false;
  }
}

// The saved link only exists for the custom target, and never when it isn't safe, so an unsafe value
// can't be written into the post body from the editor.
const urlAttribute = {
  default: '',
  parseHTML: el => el.getAttribute('data-member-button-url') ?? '',
  renderHTML: attrs => (
    attrs.memberButtonTarget === 'custom' && isSafeMemberButtonUrl(attrs.memberButtonUrl)
      ? { 'data-member-button-url': attrs.memberButtonUrl.trim() }
      : {}
  ),
};

const FIELDS = {
  label: 'memberButtonLabel',
  style: 'memberButtonStyle',
  target: 'memberButtonTarget',
  url: 'memberButtonUrl',
  'sub-text': 'memberButtonSubText',
};

export const MemberButton = Node.create({
  name: 'memberButton',

  ...blockNodeSpec('member_button', {
    attributes: {
      memberButtonLabel: dataAttribute('memberButtonLabel'),
      memberButtonStyle: dataAttribute('memberButtonStyle', { default: 'primary' }),
      memberButtonTarget: dataAttribute('memberButtonTarget', { default: 'plans' }),
      memberButtonUrl: urlAttribute,
      memberButtonSubText: dataAttribute('memberButtonSubText'),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      let currentNode = node;
      let previewTimer = null;
      let previewRequest = 0;

      const dom = document.createElement('div');
      dom.setAttribute('data-member-button-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Button</div>';

      const field = name => dom.querySelector(`[data-field="${name}"]`);
      const preview = dom.querySelector('[data-preview]');
      const hint = dom.querySelector('[data-preview-hint]');
      const urlError = dom.querySelector('[data-url-error]');

      // A button needs a label, and a custom target needs a link that is safe to save.
      const canFinish = () => {
        const { memberButtonLabel, memberButtonTarget, memberButtonUrl } = currentNode.attrs;
        if (!(memberButtonLabel || '').trim()) return false;
        return memberButtonTarget !== 'custom' || isSafeMemberButtonUrl(memberButtonUrl);
      };

      let views = null;

      const syncFromNode = () => {
        Object.entries(FIELDS).forEach(([name, attr]) => {
          const el = field(name);
          if (!el || el === document.activeElement) return;
          el.value = currentNode.attrs[attr] ?? '';
          // A saved target this podcast can't offer (gifts off, donations off) renders as the subscribe page.
          if (name === 'target' && el.value !== currentNode.attrs[attr]) el.value = 'plans';
        });
        const custom = currentNode.attrs.memberButtonTarget === 'custom';
        field('url')?.classList.toggle('hidden', !custom);
        const invalidUrl = custom && currentNode.attrs.memberButtonUrl.trim() !== ''
          && !isSafeMemberButtonUrl(currentNode.attrs.memberButtonUrl);
        urlError?.classList.toggle('hidden', !invalidUrl);
        views?.refresh();
      };

      const showPreview = html => {
        if (!preview) return;
        preview.innerHTML = html || '';
        hint?.classList.toggle('hidden', Boolean(html));
      };

      const schedulePreview = () => {
        if (!extension.options.fetchRenderedHtml || views?.view !== 'preview') return;
        clearTimeout(previewTimer);
        previewTimer = setTimeout(async () => {
          const request = ++previewRequest;
          const html = await extension.options.fetchRenderedHtml(currentNode.attrs);
          if (request === previewRequest) showPreview(html);
        }, PREVIEW_DELAY_MS);
      };

      const setAttr = (attr, value) => {
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const tr = editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, [attr]: value });
        editor.view.dispatch(tr);
      };

      Object.entries(FIELDS).forEach(([name, attr]) => {
        field(name)?.addEventListener('input', e => setAttr(attr, e.target.value));
        field(name)?.addEventListener('change', e => setAttr(attr, e.target.value));
      });

      dom.querySelector('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (pos === null || pos === undefined) return;
        const current = editor.state.doc.nodeAt(pos);
        if (current) editor.chain().focus().deleteRange({ from: pos, to: pos + current.nodeSize }).run();
      });

      // The preview is a real link; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      views = blockViews(dom, {
        canFinish,
        onChange: view => { if (view === 'preview') schedulePreview(); },
      });
      syncFromNode();
      views.setView(initialView({ hasContent: canFinish() }));

      return {
        dom,
        // Typing in the form fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, select, textarea, button')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'memberButton') return false;
          currentNode = updatedNode;
          syncFromNode();
          schedulePreview();
          return true;
        },
        destroy() {
          clearTimeout(previewTimer);
        },
      };
    };
  },

  addCommands() {
    return {
      insertMemberButton: (attrs = {}) => ({ chain }) =>
        chain().insertContent({ type: this.name, attrs }).run(),
    };
  },
});

export const memberButtonSlashItem = {
  title: 'Member button',
  description: 'A call to action: subscribe, gift, donate, or any link',
  icon: '★',
  group: 'Blocks',
  keywords: ['button', 'cta', 'member', 'upgrade', 'subscribe', 'gift', 'link'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertMemberButton().run();
  },
};
